import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { Student } from 'src/app/core/schema/Student.model';
import { AvatarComponent } from 'src/app/ui-kit/avatar/avatar.component';
import { CardComponent } from 'src/app/ui-kit/card/card/card.component';
import { ChipComponent } from 'src/app/ui-kit/chip/chip.component';
import { IconComponent } from 'src/app/ui-kit/icon/icon.component';
import { StatusDotComponent, StatusDotStatus } from 'src/app/ui-kit/status-dot/status-dot.component';

/**
 * Canonical card for a single {@link Student}. Displays the student's name, grade,
 * and an EL flag, with an optional caseload status dot reflecting data-collection
 * state. Optimized for caseload lists and roster views.
 *
 * @security FERPA — `student.firstName`, `student.lastName`, `student.dateOfBirth`,
 * `student.disabilityType`, and `student.englishLearner` are all student PII. Never
 * log, transmit, or embed these fields outside the render path.
 *
 * @feature shared
 */
@Component({
  selector: 'app-student-card',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AvatarComponent, CardComponent, ChipComponent, IconComponent, StatusDotComponent],
  host: { class: 'block' },
  template: `
    <app-card
      [button]="clickable()"
      [ariaLabel]="'Open ' + fullName() + ' profile'"
      (click)="onClick()"
    >
      <div class="flex items-start gap-3">
        <app-avatar [name]="fullName()" [colorSeed]="colorSeed()" size="lg" />

        <div class="min-w-0 flex-1">
          <div class="flex items-center gap-2">
            <h3 class="truncate text-base font-semibold text-body">{{ fullName() }}</h3>
            @if (caseloadStatus()) {
              <app-status-dot [status]="caseloadStatus()!" [ariaLabel]="caseloadStatusLabel()" />
            }
          </div>

          @if (subtitle()) {
            <p class="truncate text-sm text-muted">{{ subtitle() }}</p>
          }

          @if (flags().length) {
            <div class="mt-2 flex flex-wrap gap-1">
              @for (flag of flags(); track flag.label) {
                <app-chip [color]="flag.color" size="xs">{{ flag.label }}</app-chip>
              }
            </div>
          }
        </div>

        @if (clickable()) {
          <app-icon name="chevron-forward" size="xs" color="medium" />
        }
      </div>

      @if (areasOfNeed().length) {
        <div class="mt-3 border-t border-outline-low pt-3">
          <p class="text-xs font-semibold uppercase tracking-wide text-muted">Areas of need</p>
          <p class="mt-1 text-sm text-body">{{ areasOfNeed().join(', ') }}</p>
        </div>
      }
    </app-card>
  `,
})
export class StudentCardComponent {
  /** The student to display. @security FERPA — contains PII. */
  readonly student = input.required<Student>();

  /**
   * Optional caseload status dot shown next to the student name. Drives the
   * at-a-glance data-collection state.
   */
  readonly caseloadStatus = input<StatusDotStatus | undefined>(undefined);

  /** Human-readable label for the caseload status, used as the status dot's aria-label. */
  readonly caseloadStatusLabel = input<string>('Caseload status');

  /** Whether the card is interactive. When true, the card becomes a focusable button. @default true */
  readonly clickable = input<boolean>(true);

  /** Fires when the card is clicked (only when `clickable` is true). */
  readonly cardClick = output<Student>();

  /** Full display name, FERPA-sensitive. */
  readonly fullName = computed(() => {
    const s = this.student();
    const name = [s.firstName, s.lastName].filter(Boolean).join(' ').trim();
    return name || 'Unknown student';
  });

  /**
   * Hash seed for the avatar's deterministic background color. Keyed on the
   * student id so the colour stays stable across name edits and never
   * collides between students who happen to share a name.
   */
  readonly colorSeed = computed(() => this.student().id ?? '');

  /** Grade + school subtitle, or empty string if neither is set. */
  readonly subtitle = computed(() => {
    const s = this.student();
    const parts: string[] = [];
    if (s.grade) parts.push(`Grade ${s.grade}`);
    if (s.school) parts.push(s.school);
    return parts.join(' · ');
  });

  /** EL flag rendered as a chip. */
  readonly flags = computed<ReadonlyArray<{ label: string; color: 'purple' | 'aqua' | 'sand' | 'coral' }>>(() => {
    const s = this.student();
    const out: Array<{ label: string; color: 'purple' | 'aqua' | 'sand' | 'coral' }> = [];
    if (s.englishLearner) out.push({ label: 'EL', color: 'sand' });
    return out;
  });

  /** Deduped areas of need, capped at 3 for card display. */
  readonly areasOfNeed = computed(() => {
    const areas = this.student().areasOfNeed ?? [];
    return Array.from(new Set(areas)).slice(0, 3);
  });

  onClick(): void {
    if (this.clickable()) {
      this.cardClick.emit(this.student());
    }
  }
}
